// web/src/screens/admin/used_by_panel.ts
//
// ADMIN-07b — "Used by" panel for /admin/texts/<sc> and /admin/textsets/<sc>.
// Lists every parent entity slot that references the Text/TextSet so the
// author can see the full impact of an edit at-a-glance.
//
// Source: GET /api/admin/{texts|textsets}/{id}/refs → AdminRefDTO[].

import { adminGet, AdminError } from "../../api/admin.js";
import type { AdminRefDTO } from "../../types/api.js";

export function mountUsedByPanel(
  host: HTMLElement,
  opts: { kind: "text" | "textset"; id: string },
): () => void {
  const section = document.createElement("section");
  section.setAttribute(
    "style",
    "margin-top: 16px; padding: 12px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; max-width: 800px;",
  );
  const heading = document.createElement("h3");
  heading.textContent = "Used by";
  heading.setAttribute("style", "margin: 0 0 6px 0; font-size: 14px; color: #111827;");
  section.appendChild(heading);

  const subtitle = document.createElement("p");
  subtitle.textContent = opts.kind === "text"
    ? "Entities that reference this Text. Editing the translation here updates all of them."
    : "Entities that reference this TextSet. Editing the members here updates all of them.";
  subtitle.setAttribute("style", "margin: 0 0 10px 0; font-size: 12px; color: #6b7280;");
  section.appendChild(subtitle);

  const body = document.createElement("div");
  const loading = document.createElement("p");
  loading.textContent = "Loading…";
  loading.setAttribute("style", "margin: 0; font-size: 13px; color: #6b7280;");
  body.appendChild(loading);
  section.appendChild(body);

  host.appendChild(section);

  let disposed = false;

  void (async () => {
    const url = opts.kind === "text"
      ? `/api/admin/texts/${encodeURIComponent(opts.id)}/refs`
      : `/api/admin/textsets/${encodeURIComponent(opts.id)}/refs`;
    let refs: AdminRefDTO[];
    try {
      refs = await adminGet<AdminRefDTO[]>(url);
    } catch (err) {
      if (disposed) return;
      body.replaceChildren();
      const p = document.createElement("p");
      p.setAttribute("style", "margin: 0; font-size: 13px; color: #991b1b;");
      p.textContent = err instanceof AdminError
        ? `Failed to load references (${err.status})`
        : `Failed to load references: ${(err as Error).message}`;
      body.appendChild(p);
      return;
    }
    if (disposed) return;
    body.replaceChildren();
    if (refs.length === 0) {
      const p = document.createElement("p");
      p.setAttribute("style", "margin: 0; font-size: 13px; color: #6b7280; font-style: italic;");
      p.textContent = "(not referenced by any game or task — safe to delete)";
      body.appendChild(p);
      return;
    }
    const ul = document.createElement("ul");
    ul.setAttribute("style", "margin: 0; padding-left: 20px; font-size: 13px; color: #111827;");
    for (const r of refs) {
      const li = document.createElement("li");
      li.setAttribute("style", "margin-bottom: 4px;");

      const kindTag = document.createElement("span");
      kindTag.textContent = r.kind;
      kindTag.setAttribute(
        "style",
        "font-size: 11px; color: #6b7280; background: #e5e7eb; padding: 1px 6px; border-radius: 3px; margin-right: 6px; font-family: ui-monospace, monospace;",
      );
      li.appendChild(kindTag);

      const link = makeRefLink(r);
      li.appendChild(link);

      if (r.slot) {
        const slotSpan = document.createElement("span");
        slotSpan.textContent = ` · slot=${r.slot}`;
        slotSpan.setAttribute("style", "color: #6b7280; font-family: ui-monospace, monospace; font-size: 12px;");
        li.appendChild(slotSpan);
      }
      ul.appendChild(li);
    }
    body.appendChild(ul);
  })();

  return () => {
    disposed = true;
    section.remove();
  };
}

function makeRefLink(r: AdminRefDTO): HTMLElement {
  // Resolve a navigable admin URL for this reference. game / game_member →
  // /admin/games/<sc>. task / task_member / text_with_effect →
  // /admin/games/<gsc>/cards/<csc>/tasks/<tsc> when we have the chain,
  // otherwise fall back to the game page.
  let href = "";
  let label = r.parent_short_code;
  if (r.kind === "game" || r.kind === "game_member") {
    href = `/admin/games/${encodeURIComponent(r.parent_short_code)}`;
    label = r.parent_short_code;
  } else if ((r.kind === "task" || r.kind === "task_member" || r.kind === "text_with_effect")
    && r.game_short_code && r.card_short_code) {
    href = `/admin/games/${encodeURIComponent(r.game_short_code)}/cards/${encodeURIComponent(r.card_short_code)}/tasks/${encodeURIComponent(r.parent_short_code)}`;
    label = `${r.game_short_code} · ${r.card_short_code} · ${r.parent_short_code}`;
  } else {
    // Fallback: render plain text (no navigable chain).
    const span = document.createElement("span");
    span.textContent = label;
    span.setAttribute("style", "font-family: ui-monospace, monospace;");
    return span;
  }
  const a = document.createElement("a");
  a.href = href;
  a.textContent = label;
  a.setAttribute("style", "color: #2563eb; text-decoration: none; font-family: ui-monospace, monospace;");
  a.addEventListener("click", (e) => {
    e.preventDefault();
    history.pushState({}, "", href);
    window.dispatchEvent(new PopStateEvent("popstate"));
  });
  return a;
}
